import React from 'react';
import { IoAdd } from 'react-icons/io5';

import { Button, Group, MantineProvider, Text } from '@mantine/core';

import { ComponentSection, PermissionComponent } from 'models';
import { CollectionHelpers } from 'src/helpers/CollectionHelpers';

import { DatasheetMode, useDatasheetMode } from 'components/datasheet/DatasheetMode';
import { DesignLibraryTeaser } from 'components/design-library-teaser/DesignLibraryTeaser';
import { ProjectFinder } from 'components/project-finder/ProjectFinder';
import { ReferenceDesignButton } from 'components/reference-design-button/ReferenceDesignButton';
import { CarouselSection } from 'components/section/CarouselSection';
import { useComponentPermissions } from 'hooks/use-component-permissions';
import { useReferenceDesignsByComponent } from 'hooks/use-reference-designs-by-component';
import { ProjectService } from 'services/ProjectService';

import { useComponentContext } from '../../hooks/use-component-context';
import { ComponentDatasheetSection } from './ComponentDatasheetSection';

const ReferenceDesigns = () => {
    const { component } = useComponentContext();

    const mode = useDatasheetMode();

    const permissions = useComponentPermissions(component);
    const canEdit = permissions[PermissionComponent.EDIT];

    const { projects: referenceDesigns } = useReferenceDesignsByComponent(component.id, canEdit);

    if (!referenceDesigns.length && !canEdit) return null;

    if (mode !== DatasheetMode.VIEW) {
        return null;
    }

    const communityDesigns = referenceDesigns.filter(
        (project) => !CollectionHelpers.equals(project.template.profile, component.manufacturer),
    );

    const manufacturerDesigns = referenceDesigns.filter((project) =>
        CollectionHelpers.equals(project.template.profile, component.manufacturer),
    );

    return (
        // override DatasheetMantineProvider
        <MantineProvider
            theme={{
                components: {
                    Checkbox: {
                        defaultProps: {
                            disabled: false,
                        },
                    },
                    Button: {
                        styles: () => ({}),
                    },
                    Pill: {
                        styles: () => ({}),
                    },
                },
            }}
        >
            <ComponentDatasheetSection alwaysShow title="Reference Designs" id={ComponentSection.PROJECTS}>
                <Group gap="xs">
                    <ReferenceDesignButton variant="filled" component={component} />
                    {canEdit && (
                        <ProjectFinder
                            onSelect={(project) => {
                                ProjectService.navigate.editor(project.id, 'publish').then();
                            }}
                        >
                            {(open) => (
                                <Button variant="outline" onClick={open} leftSection={<IoAdd />}>
                                    Select an existing project
                                </Button>
                            )}
                        </ProjectFinder>
                    )}
                </Group>

                {!!referenceDesigns.length && (
                    <CarouselSection loop nbCols={4}>
                        {[...manufacturerDesigns, ...communityDesigns].map((project) => (
                            <DesignLibraryTeaser
                                isLight
                                showPublishedStatus={canEdit}
                                project={project}
                                key={project.id}
                            />
                        ))}
                    </CarouselSection>
                )}

                {!referenceDesigns.length && <Text c="dimmed">No reference designs yet.</Text>}
            </ComponentDatasheetSection>
        </MantineProvider>
    );
};

export { ReferenceDesigns };
